import React, { useEffect, useState } from 'react';
import {
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'i18n/TranslationProvider';
import {
  FiList,
  FiBarChart2,
  FiTrendingUp,
  FiUsers,
  FiFileText,
  FiCheckCircle,
} from 'react-icons/fi';
import { PageShell, PageHeader } from 'components/layout/PageShell';
import { UnifiedLoadingState, UnifiedStatCard } from 'components/layout/UnifiedPrimitives';
import API from 'api/api';

const KPI_DEFAULTS = {
  activeListings: 0,
  quotesToday: 0,
  revenueMonth: 0,
  pendingPartners: 0,
  unpaidInvoices: 0,
  completedTransports: 0,
};

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [kpis, setKpis] = useState(KPI_DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [listingsRes, pendingPartnersRes] = await Promise.allSettled([
          API.get('/listings', { params: { status: 'open', limit: 1 } }),
          API.get('/users', { params: { role: 'partner', validationStatus: 'pending', limit: 1 } }),
        ]);

        if (cancelled) return;

        const activeListings =
          listingsRes.status === 'fulfilled'
            ? listingsRes.value?.data?.meta?.total ??
              listingsRes.value?.data?.data?.meta?.total ??
              0
            : 0;

        const pendingPartners =
          pendingPartnersRes.status === 'fulfilled'
            ? pendingPartnersRes.value?.data?.meta?.total ??
              pendingPartnersRes.value?.data?.data?.meta?.total ??
              0
            : 0;

        setKpis((prev) => ({ ...prev, activeListings, pendingPartners }));
      } catch {
        // silently fall back to zero values
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = [
    {
      label: t('Active Listings'),
      value: kpis.activeListings,
      helpText: t('Open transport requests'),
      icon: FiList,
      accent: 'blue.500',
    },
    {
      label: t('Quotes Today'),
      value: kpis.quotesToday,
      helpText: t('Submitted in last 24h'),
      icon: FiBarChart2,
      accent: 'orange.500',
    },
    {
      label: t('Platform Revenue (MTD)'),
      value: `€${kpis.revenueMonth.toLocaleString()}`,
      helpText: t('Month to date'),
      icon: FiTrendingUp,
      accent: 'green.500',
    },
    {
      label: t('Pending Partners'),
      value: kpis.pendingPartners,
      helpText: t('Awaiting approval'),
      icon: FiUsers,
      accent: 'purple.500',
    },
    {
      label: t('Unpaid Invoices'),
      value: kpis.unpaidInvoices,
      helpText: t('Requires action'),
      icon: FiFileText,
      accent: 'red.500',
    },
    {
      label: t('Completed Transports'),
      value: kpis.completedTransports,
      helpText: t('All time delivered'),
      icon: FiCheckCircle,
      accent: 'teal.500',
    },
  ];

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={t('Admin Dashboard')}
          description={t('Platform overview and key performance indicators.')}
        />

        <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} spacing={4}>
          {cards.map((card) => (
            loading ? (
              <UnifiedLoadingState
                key={card.label}
                label={t('Loading dashboard metrics...')}
                minH="140px"
              />
            ) : (
              <UnifiedStatCard key={card.label} {...card} />
            )
          ))}
        </SimpleGrid>
      </VStack>
    </PageShell>
  );
}
